import type { Tile } from '../../game/types.js';
import { tileKey } from '../../game/types.js';
import type { AiAction } from '../types.js';

/**
 * Compact, string-keyed encoding of every action the ISMCTS tree edge tracks.
 * The tree is shared across determinizations: every unique (tile, arm) pair
 * the AI could ever play is a candidate edge. Using a string key lets us
 * use a plain Map (no custom hasher) on each Node.
 *
 * - `lead:1-3` — lead with tile [1,3].
 * - `play:1-3:0` — play [1,3] on arm 0.
 * - `draw` / `pass` / `claim` — non-tile actions.
 *
 * `claim` is only emitted by the search when a Muggins window opens. In
 * practice ISMCTS auto-settles pendingMuggins after every play (the search
 * assumes the scorer always claims its own points), so claim edges are only
 * generated when the AI itself owns the window AND the search exposes the
 * decision explicitly. For V1 we keep auto-claim and never expose claim
 * edges in the tree.
 */
export type ActionKey = string;

export function actionKey(action: AiAction): ActionKey {
  switch (action.kind) {
    case 'play':
      return action.armIndex < 0
        ? `lead:${tileKey(action.tile)}`
        : `play:${tileKey(action.tile)}:${action.armIndex}`;
    case 'draw':
      return 'draw';
    case 'pass':
      return 'pass';
    case 'claim-muggins':
      return 'claim';
  }
}

export function decodePlayKey(key: ActionKey): { tile: Tile; armIndex: number } | null {
  if (key.startsWith('lead:')) {
    const rest = key.slice('lead:'.length);
    const parts = rest.split('-').map(Number);
    if (parts.length !== 2) return null;
    const [a, b] = parts;
    if (a === undefined || b === undefined) return null;
    return { tile: clampTile(a, b), armIndex: -1 };
  }
  if (key.startsWith('play:')) {
    const rest = key.slice('play:'.length);
    const [tilePart, armPart] = rest.split(':');
    if (tilePart === undefined || armPart === undefined) return null;
    const parts = tilePart.split('-').map(Number);
    if (parts.length !== 2) return null;
    const [a, b] = parts;
    if (a === undefined || b === undefined) return null;
    return { tile: clampTile(a, b), armIndex: Number(armPart) };
  }
  return null;
}

function clampTile(a: number, b: number): Tile {
  const low = Math.min(a, b);
  const high = Math.max(a, b);
  if (low < 0 || low > 6 || high < 0 || high > 6) {
    throw new Error(`decodePlayKey: invalid pips ${a}, ${b}`);
  }
  return [low as 0 | 1 | 2 | 3 | 4 | 5 | 6, high as 0 | 1 | 2 | 3 | 4 | 5 | 6];
}
